import { apiClient } from '@/lib/api/client';
import { API_ENDPOINTS } from '@/lib/api/config';
import type { ApiResponse } from '@/lib/api/types';
import type {
  ScanBatch,
  ScanBatchListParams,
  ScanBatchMetrics,
  BatchPageRecord,
  QrBboxData
} from '../types';

const { PHOTO_SCANNING } = API_ENDPOINTS;

export const photoScanningApi = {
  getList: (params?: ScanBatchListParams) =>
    apiClient.getPaginated<ScanBatch>(PHOTO_SCANNING.LIST, params),

  getById: (id: number) =>
    apiClient.get<ScanBatch & { pages: BatchPageRecord[] }>(
      PHOTO_SCANNING.GET(id)
    ),

  getMetrics: () => apiClient.get<ScanBatchMetrics>(PHOTO_SCANNING.METRICS),

  startBatch: (id: number) =>
    apiClient.post<ScanBatch>(PHOTO_SCANNING.START(id), {}),

  scanPage: (id: number, data?: { qr_code?: string }) =>
    apiClient.post<BatchPageRecord>(PHOTO_SCANNING.SCAN_PAGE(id), data || {}),

  completeBatch: (id: number) =>
    apiClient.post<ScanBatch>(PHOTO_SCANNING.COMPLETE(id), {}),

  // Send a batch's registration to the repair queue. The registration is in
  // the URL; only destination/reason/notes ride in the body.
  refuse: (
    registrationId: number,
    data: {
      repair_destination_id: number;
      repair_reason_id: number;
      notes?: string;
    }
  ) => apiClient.post<unknown>(PHOTO_SCANNING.REFUSE(registrationId), data),

  delete: (id: number) => apiClient.delete(PHOTO_SCANNING.DELETE(id)),

  scanPageFromCamera: (
    id: number,
    image: Blob,
    options?: {
      expectedSequence?: number;
      qrCode?: string | null;
      qrBbox?: QrBboxData | null;
    },
    onProgress?: (progress: number) => void
  ) => {
    const formData = new FormData();
    formData.append('image', image, 'capture.jpg');
    if (options?.expectedSequence !== undefined) {
      formData.append('expected_sequence', String(options.expectedSequence));
    }
    if (options?.qrCode) {
      formData.append('qr_code', options.qrCode);
    }
    if (options?.qrBbox) {
      // Serialised as a single JSON string so the request validation can
      // treat it as a size-capped scalar. The service parses + validates the
      // shape before persisting.
      formData.append('qr_bbox', JSON.stringify(options.qrBbox));
    }
    return apiClient.uploadFormData<BatchPageRecord>(
      PHOTO_SCANNING.SCAN_FROM_CAMERA(id),
      formData,
      onProgress
    );
  },

  scanPagesFromImages: async (
    id: number,
    images: Blob[],
    qrCodes?: Array<string | null>,
    qrBboxes?: Array<QrBboxData | null>,
    onProgress?: (progress: number) => void
  ) => {
    // PHP's `max_file_uploads` ini directive (default 20) silently drops
    // files beyond the limit AND leaks a warning into the response body —
    // which corrupts the JSON the apiClient parses. Chunk the upload below
    // the default ceiling so a single request never trips that path,
    // regardless of how php.ini is configured on the host.
    //
    // Each image carries 3 form fields (`images[]`, `qr_codes[]`,
    // `qr_bboxes[]`), but only `images[]` counts toward `max_file_uploads`
    // — so a chunk size of 15 leaves a five-image safety margin under the
    // default 20-file ceiling.
    const CHUNK_SIZE = 15;

    const buildChunkFormData = (start: number, end: number): FormData => {
      const formData = new FormData();
      for (let i = start; i < end; i++) {
        const img = images[i];
        const ext =
          img instanceof File && img.name.includes('.')
            ? img.name.split('.').pop()
            : 'jpg';
        const filename =
          img instanceof File ? img.name : `page-${i + 1}.${ext}`;
        formData.append('images[]', img, filename);
        // Send a parallel qr_codes[] entry for every image so positions
        // stay aligned even when individual decodes failed. Blank string =
        // no QR decoded (the backend treats it as unverified).
        const qr = qrCodes?.[i] ?? null;
        formData.append('qr_codes[]', qr ?? '');
        // Parallel qr_bboxes[]: each entry is a JSON-encoded `{corners}`
        // payload (or blank when the frontend couldn't locate the QR).
        const bbox = qrBboxes?.[i] ?? null;
        formData.append('qr_bboxes[]', bbox ? JSON.stringify(bbox) : '');
      }
      return formData;
    };

    if (images.length === 0) {
      // Let the backend reject empty uploads with its existing validation.
      return apiClient.uploadFormData<BatchPageRecord[]>(
        PHOTO_SCANNING.SCAN_FROM_IMAGES(id),
        buildChunkFormData(0, 0)
      );
    }

    // Sequential — running chunks in parallel would just create the same
    // server load with more failure modes (partial commit if a middle
    // chunk fails). Sequential keeps the merge order deterministic and
    // lets the backend's per-batch lock keep `sequence` slots correct.
    let lastResponse: Awaited<
      ReturnType<typeof apiClient.uploadFormData<BatchPageRecord[]>>
    > | null = null;
    const merged: BatchPageRecord[] = [];

    // Byte-weighted aggregation so the caller sees one smooth 0→100 across
    // every chunk instead of a per-chunk reset. Each chunk contributes its
    // share of the total upload size; within a chunk the XHR percent scales
    // that share, and finished chunks add their full weight to `completedBytes`.
    const totalBytes = images.reduce((sum, img) => sum + img.size, 0);
    let completedBytes = 0;

    for (let start = 0; start < images.length; start += CHUNK_SIZE) {
      const end = Math.min(start + CHUNK_SIZE, images.length);
      let chunkBytes = 0;
      for (let i = start; i < end; i++) chunkBytes += images[i].size;
      const chunkStartBytes = completedBytes;
      const response = await apiClient.uploadFormData<BatchPageRecord[]>(
        PHOTO_SCANNING.SCAN_FROM_IMAGES(id),
        buildChunkFormData(start, end),
        onProgress && totalBytes > 0
          ? (chunkPct) =>
              onProgress(
                Math.round(
                  ((chunkStartBytes + (chunkBytes * chunkPct) / 100) /
                    totalBytes) *
                    100
                )
              )
          : undefined
      );
      completedBytes += chunkBytes;
      lastResponse = response;
      if (Array.isArray(response?.data)) {
        merged.push(...response.data);
      }
    }

    // Guarantee a terminal 100% — covers the `totalBytes === 0` case (all
    // zero-byte blobs, so no per-chunk events fire) and any rounding that
    // left the last event a hair under 100.
    onProgress?.(100);

    // Return the last response's envelope (status, message, etc.) but
    // with `data` rebuilt to span every chunk — so callers see one logical
    // upload regardless of how many round-trips the chunker made.
    return {
      ...(lastResponse ?? { success: true, status: 201, data: [] }),
      data: merged
    } as ApiResponse<BatchPageRecord[]>;
  },

  rotatePage: (batchId: number, pageId: number, degrees: number) =>
    apiClient.post<BatchPageRecord>(
      PHOTO_SCANNING.ROTATE_PAGE(batchId, pageId),
      { degrees }
    ),

  enhancePage: (
    batchId: number,
    pageId: number,
    image: Blob,
    onProgress?: (progress: number) => void
  ) => {
    const formData = new FormData();
    formData.append('image', image, 'upload.jpg');
    return apiClient.uploadFormData<BatchPageRecord>(
      PHOTO_SCANNING.ENHANCE_PAGE(batchId, pageId),
      formData,
      onProgress
    );
  },

  deletePage: (batchId: number, pageId: number) =>
    apiClient.delete(PHOTO_SCANNING.DELETE_PAGE(batchId, pageId)),

  reorderPages: (batchId: number, pageIds: number[]) =>
    apiClient.post<ScanBatch & { pages: BatchPageRecord[] }>(
      PHOTO_SCANNING.REORDER_PAGES(batchId),
      { page_ids: pageIds }
    ),

  bulkDelete: (data: {
    ids?: number[];
    all?: boolean;
    filters?: Record<string, unknown>;
  }) => apiClient.post(PHOTO_SCANNING.BULK_DELETE, data),

  // Self-healing backfill — the viewer calls this once per legacy page after a
  // successful client-side decode so future loads render the overlay
  // instantly from the stored bbox instead of re-decoding the served image.
  updateQrBbox: (batchId: number, pageId: number, qrBbox: QrBboxData) =>
    apiClient.put<BatchPageRecord>(
      PHOTO_SCANNING.UPDATE_QR_BBOX(batchId, pageId),
      { qr_bbox: qrBbox }
    )
};
